import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import LoginActivityPanel from '../../common/LoginActivityPanel';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchCustomerDetails } from '../../../store/slices/usersSlice';
import { formatRawDateTime } from '../../../utils/format';
import {
    CheckCircle,
    AlertCircle,
    BarChart2
} from 'lucide-react';
import Loader from '../../common/Loader';
import MaskedMobile from '../../common/MaskedMobile';
import ImageViewerModal from '../../common/ImageViewerModal';
import UserProfileCard from '../../common/UserProfileCard';

// ---------------------------------------------------------------------------
// InfoItem helper
// ---------------------------------------------------------------------------
const InfoItem = ({
    label,
    value,
    className = '',
}: {
    label: string;
    value?: React.ReactNode;
    className?: string;
}) => (
    <div className={`flex flex-col min-w-0 ${className}`}>
        <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-1 truncate" title={label}>
            {label}
        </span>
        <span className="text-sm font-semibold text-slate-800 break-words [overflow-wrap:anywhere] leading-relaxed">
            {value || '-'}
        </span>
    </div>
);

// ---------------------------------------------------------------------------
// CustomerDetailsPage
// ---------------------------------------------------------------------------
const CustomerDetailsPage: React.FC = () => {
    const { mobile } = useParams<{ mobile: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const fromPage = (location.state as any)?.fromPage || 'Users';
    const dispatch = useAppDispatch();
    const { customerDetails, customerDetailsLoading, customerDetailsError } = useAppSelector(
        (state) => state.users
    );
    const [viewingImage, setViewingImage] = useState<string | null>(null);


    useEffect(() => {
        if (mobile) {
            dispatch(fetchCustomerDetails(mobile));
        }

        return () => {
            // Optional: clear details on unmount if we want fresh fetch next time
            // dispatch(clearCustomerDetails());
        };
    }, [dispatch, mobile]);

    if (customerDetailsLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <Loader />
            </div>
        );
    }

    if (customerDetailsError) {
        return (
            <div className="p-8 text-center">
                <div className="text-red-500 mb-4">Error: {customerDetailsError}</div>
                <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-200 rounded">
                    Go Back
                </button>
            </div>
        );
    }

    if (!customerDetails || !customerDetails.user) {
        return null;
    }

    const { user, stats, orders, referrals } = customerDetails;


    return (
        <div className="w-full pb-12 font-sans">
            {/* Page header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Customer Details</h1>
                <nav className="flex items-center gap-1.5 text-sm">
                    <button
                        onClick={() => navigate(-1)}
                        className="text-slate-500 hover:text-slate-800 font-medium transition-colors"
                    >
                        {fromPage}
                    </button>
                    <span className="text-slate-300 select-none">›</span>
                    <span className="text-slate-800 font-semibold">Customer Details</span>
                </nav>
            </div>

            <div className="space-y-6">
                {/* ── Basic / KYC / Bank Details ─────────────────────────── */}
                <UserProfileCard user={user} />

                {/* ── Statistics ───────────────────────────────────────────── */}
                <div>
                    <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-3">
                        STATISTICS
                    </h2>
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
                                <BarChart2 size={17} />
                            </div>
                            <p className="font-bold text-slate-800 text-sm">Account Statistics</p>
                        </div>
                        <div className="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                            <InfoItem label="Total Orders" value={stats.total_orders} />
                            <InfoItem label="Purchased Orders" value={stats.purchased_orders} />
                            <InfoItem label="Total Referrals" value={stats.total_referrals} />
                            <InfoItem label="Total Coins" value={user.coins ?? 0} />
                        </div>
                    </div>
                </div>

                {/* ── Recent Orders ─────────────────────────────────────────── */}
                <div>
                    <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-3">
                        RECENT ORDERS
                    </h2>
                    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-slate-50 border-b-2 border-slate-200">
                                <tr>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        S.No
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        Order ID
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        Date
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        Units
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        Buffalo / Calf
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        Coins
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        Amount
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        Nominees
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="px-4 py-6 text-center text-slate-400 text-sm">
                                            No orders found
                                        </td>
                                    </tr>
                                ) : (
                                    orders.map((order: any, idx: number) => {
                                        const nomineeCount = order.nominees?.length || 0;
                                        const totalUnits = order.numUnits || 0;
                                        const nomineesPendingCount = Math.max(totalUnits - nomineeCount, 0);
                                        // Pending units aren't represented in this payload (only units that
                                        // already have a nominee are), so only the added ones' unit ids show here.
                                        const nomineeUnitIds: string[] = (order.nominees || [])
                                            .map((n: any) => n.unit_number || (n.unitIndex != null ? `Unit ${n.unitIndex + 1}` : null))
                                            .filter(Boolean);
                                        return (
                                        <tr
                                            key={order.id}
                                            onClick={() => navigate(`/orders/${encodeURIComponent(order.id)}`, { state: { fromPage: 'Customer Details' } })}
                                            className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                                        >
                                            <td className="px-4 py-3 text-sm text-slate-500 font-medium whitespace-nowrap">
                                                {idx + 1}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-[#1B4332] font-medium hover:underline whitespace-nowrap">
                                                {order.id}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                                                {formatRawDateTime(order.placedAt)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{order.numUnits}</td>
                                            <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                                                {order.buffaloCount} / {order.calfCount}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-blue-600 font-medium whitespace-nowrap">
                                                {order.coinsRedeemed ? order.coinsRedeemed : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                                                ₹{order.totalCost?.toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                                                <span
                                                    className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${
                                                        order.status === 'APPROVED'
                                                            ? 'bg-green-100 text-green-800'
                                                            : order.status === 'REJECTED'
                                                            ? 'bg-red-100 text-red-800'
                                                            : 'bg-blue-100 text-blue-800'
                                                    }`}
                                                >
                                                    {order.status || 'PENDING'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                                                <div className="flex flex-col gap-0.5 whitespace-nowrap">
                                                    <span className={`text-xs font-semibold whitespace-nowrap ${nomineesPendingCount > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                                        {nomineeCount}/{totalUnits} Added{nomineesPendingCount > 0 ? `, ${nomineesPendingCount} Pending` : ''}
                                                    </span>
                                                    {nomineeUnitIds.length > 0 && (
                                                        <span className="text-[10px] text-slate-400 truncate max-w-[180px]" title={nomineeUnitIds.join(', ')}>
                                                            Units: {nomineeUnitIds.join(', ')}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ── Referrals ─────────────────────────────────────────────── */}
                <div>
                    <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-3">
                        REFERRALS
                    </h2>
                    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-slate-50 border-b-2 border-slate-200">
                                <tr>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        Name
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        Mobile
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        City
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        Total Coins
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        Paid Units
                                    </th>
                                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                        Verified
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {referrals.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-6 text-center text-slate-400 text-sm">
                                            No referrals found
                                        </td>
                                    </tr>
                                ) : (
                                    referrals.map((ref: any) => (
                                        <tr
                                            key={ref.id}
                                            className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                                        >
                                            <td className="px-4 py-3 text-sm text-slate-700 font-medium whitespace-nowrap">
                                                {ref.first_name} {ref.last_name}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                                                <MaskedMobile mobile={ref.mobile} countryCode={ref.country_code} className="text-sm font-semibold text-slate-700" />
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{ref.city}</td>
                                            <td className="px-4 py-3 text-sm text-blue-600 font-semibold whitespace-nowrap">
                                                {ref.coins || 0}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-bold text-slate-700 whitespace-nowrap">
                                                {ref.total_paid_units || 0}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                                                {ref.verified ? (
                                                    <CheckCircle size={16} className="mx-auto text-green-500" />
                                                ) : (
                                                    <AlertCircle size={16} className="mx-auto text-yellow-500" />
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ── Login Activity ────────────────────────────────────────── */}
                {mobile && <LoginActivityPanel mobile={mobile} />}
            </div>
            <ImageViewerModal url={viewingImage} onClose={() => setViewingImage(null)} />
        </div>
    );
};

export default CustomerDetailsPage;
